import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, Clock, FileText, X, Eye, DollarSign, User } from 'lucide-react';
import * as fieldTeamApi from '../../services/fieldTeamApi';
import './FieldTeam.css';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  approved: { label: 'Approved', color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
  rejected: { label: 'Rejected', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' }
};

export default function Clearance() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fieldTeamApi.getAllClearances({ status: statusFilter });
      setRequests(res.data?.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const handleReview = async (id, status) => {
    try {
      await fieldTeamApi.reviewClearance(id, { status, review_notes: reviewNotes });
      setSelectedRequest(null);
      setReviewNotes('');
      fetchRequests();
    } catch (err) { alert(err.response?.data?.error || err.message); }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="ft-page">
      {/* Stats */}
      <div className="stat-blocks">
        <div className="stat-block" style={{ '--accent': '#f59e0b' }}>
          <div className="stat-icon"><Clock size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{pendingCount}</span>
            <span className="stat-label">Pending Review</span>
          </div>
        </div>
        <div className="stat-block" style={{ '--accent': '#22c55e' }}>
          <div className="stat-icon"><CheckCircle size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{requests.filter(r => r.status === 'approved').length}</span>
            <span className="stat-label">Approved</span>
          </div>
        </div>
        <div className="stat-block" style={{ '--accent': '#ef4444' }}>
          <div className="stat-icon"><XCircle size={20} /></div>
          <div className="stat-content">
            <span className="stat-value">{requests.filter(r => r.status === 'rejected').length}</span>
            <span className="stat-label">Rejected</span>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
        <select className="input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ width: '160px' }}>
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Table */}
      <div className="ft-table-wrapper">
        <table className="ft-table">
          <thead>
            <tr>
              <th>Member</th>
              <th>Client</th>
              <th>Invoice</th>
              <th>Amount Claimed</th>
              <th>Receipt</th>
              <th>Status</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px' }}>Loading...</td></tr>
            ) : requests.length === 0 ? (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>No clearance requests</td></tr>
            ) : requests.map(req => {
              const cfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
              return (
                <tr key={req.id}>
                  <td><User size={12} style={{ marginRight: '4px', opacity: 0.5 }} />{req.member_name || '-'}</td>
                  <td>{req.company_name || '-'}</td>
                  <td>{req.invoice_number || `#${req.field_invoice_id}`}</td>
                  <td style={{ fontWeight: 600 }}>PKR {parseFloat(req.amount_claimed).toLocaleString()}</td>
                  <td>
                    {req.receipt_url ? (
                      <a href={req.receipt_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)' }}>
                        <FileText size={14} /> View
                      </a>
                    ) : '-'}
                  </td>
                  <td>
                    <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600,
                      color: cfg.color, background: cfg.bg }}>{cfg.label}</span>
                  </td>
                  <td style={{ fontSize: '12px' }}>{new Date(req.created_at).toLocaleDateString()}</td>
                  <td>
                    {req.status === 'pending' ? (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button className="btn btn-sm" style={{ color: '#22c55e' }} title="Approve"
                          onClick={() => { setSelectedRequest(req); setReviewNotes(''); }}>
                          <CheckCircle size={14} />
                        </button>
                        <button className="btn btn-sm" style={{ color: '#ef4444' }} title="Reject"
                          onClick={() => { setSelectedRequest({ ...req, _action: 'reject' }); setReviewNotes(''); }}>
                          <XCircle size={14} />
                        </button>
                      </div>
                    ) : (
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        {req.reviewed_at ? new Date(req.reviewed_at).toLocaleDateString() : '-'}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Review Modal */}
      {selectedRequest && (
        <div className="modal-overlay" onClick={() => setSelectedRequest(null)}>
          <div className="modal" style={{ maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedRequest._action === 'reject' ? 'Reject' : 'Approve'} Clearance</h3>
              <button className="close-btn" onClick={() => setSelectedRequest(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: '13px', marginBottom: '16px', lineHeight: 1.6 }}>
                <div><strong>Member:</strong> {selectedRequest.member_name}</div>
                <div><strong>Client:</strong> {selectedRequest.company_name}</div>
                <div><strong>Amount:</strong> PKR {parseFloat(selectedRequest.amount_claimed).toLocaleString()}</div>
                {selectedRequest.notes && <div><strong>Notes:</strong> {selectedRequest.notes}</div>}
              </div>
              <div className="form-group">
                <label>Review Notes</label>
                <textarea className="input" rows={3} value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
                  placeholder="Optional notes for the team member..." />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setSelectedRequest(null)}>Cancel</button>
              {selectedRequest._action === 'reject' ? (
                <button className="btn" style={{ background: '#ef4444', color: 'white' }}
                  onClick={() => handleReview(selectedRequest.id, 'rejected')}>
                  <XCircle size={14} /> Reject
                </button>
              ) : (
                <button className="btn" style={{ background: '#22c55e', color: 'white' }}
                  onClick={() => handleReview(selectedRequest.id, 'approved')}>
                  <CheckCircle size={14} /> Approve
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
